import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthenticationService } from '@app/core/auth';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { take } from 'rxjs';

@Component({
  selector: 'app-header-right',
  imports: [
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatDividerModule,
    NgScrollbarModule,
    TranslatePipe
  ],
  template: `
    <div>
      <button id="step-user" mat-icon-button [matMenuTriggerFor]="userMenu" aria-label="Menú de usuario" style="padding: 0;">
        @if (userInitials()) {
          <div class="avatar-initials" [attr.aria-label]="'Avatar de ' + user()?.userName">
            {{ userInitials() }}
          </div>
        }
      </button>

      <mat-menu #userMenu="matMenu" xPosition="before" class="dropdown-user-profile">
          <div class="dropdown-header">
            <h3 class="dropdown-user-profile__title">{{ 'Perfil' | translate }}</h3>
          </div>
          <ng-scrollbar visibility="hover">
            <div class="dropdown-body">
              <div class="dropdown-user-profile__info">
                @if (userInitials()) {
                  <div class="avatar-initials" aria-hidden="true">
                    {{ userInitials() }}
                  </div>
                }
                <div class="dropdown-user-profile__details">
                  <div class="dropdown-user-profile__name">{{ user()?.userName }}</div>
                  <p class="dropdown-user-profile__email">{{ user()?.email }}</p>
                </div>
              </div>
              <mat-divider class="dropdown-user-profile__divider"></mat-divider>
              <div class="grid">
                <button mat-flat-button color="primary" class="dropdown-user-profile__logout" (click)="logout()">
                  <div class="flex-inline align-item-center">
                    <mat-icon class="m-r-10">exit_to_app</mat-icon>
                    {{ 'Cerrar sesión' | translate }}
                  </div>
                </button>
              </div>
            </div>
          </ng-scrollbar>
      </mat-menu>
    </div>
  `,
  styles: [
    `
      .avatar-initials {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 36px;
        border-radius: 50%;
        background-color: var(--primary-500);
        color: white;
        font-size: clamp(0.8rem, 1.2vw, 1rem);
        font-weight: 600;
        line-height: 1;
        overflow: hidden;
        -webkit-user-select: none;
        user-select: none;
        padding: 0px 8px;
      }

      .dropdown-user-profile {
        min-width: 350px;
        max-width: 100%;

        &__title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0;
        }

        &__info {
          display: flex;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        &__details {
          margin-left: 12px;
        }

        &__name,
        &__email {
          max-width: 180px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          display: block;
        }

        &__name {
          font-weight: 600;
          margin: 0;
        }

        &__email {
          font-size: 0.875rem;
          color: var(--text-muted);
          margin: 0;
        }

        &__divider {
          margin: 1rem !important;
        }

        &__logout {
          width: 100%;

          &.mdc-button {
            border-radius: 20px;
          }
        }
      }

      .dropdown-header,
      .dropdown-body {
        padding: 8px 20px;
      }

      @media (max-width: 575.98px) {
        .header-item {
          &.mdc-button {
            min-width: 50px;
            padding: 0;
          }
        }

        .pc-header .mdc-button {
          min-width: 0px !important;
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavRightComponent {
  private readonly authService = inject(AuthenticationService);
  readonly user = computed(() => this.authService.currentUser()?.user);
  readonly userInitials = computed(() => {
    const name = this.user()?.userName?.trim();
    if (!name) {
      return '..';
    }

    const parts = name.split(/\s+/);
    const firstInitial = parts[0]?.[0] ?? '';
    const secondInitial = parts.length > 1 ? parts[1][0] : '';
    return `${firstInitial}${secondInitial}`.toUpperCase();
  });

  logout(): void {
    this.authService.logout().pipe(take(1)).subscribe();
  }
}
